/**
 * Pinning a teammate to one side of a release in progress (infra-config docs/progressive-delivery-plan.md).
 *
 * While Argo Rollouts runs a canary, two builds serve the site: stable and the new release. Envoy sends a request
 * carrying `gz_preview=canary|stable` to that side, ahead of the visitor's automatic placement (`gz_track`). The
 * same probe header the rollout gate uses tells the two builds apart: `x-kuder-canary: 1` reaches the canary while
 * a release is in progress (stable otherwise), `x-kuder-canary: 0` always reaches stable.
 */

export type ReleaseSide = 'stable' | 'canary';

export const PREVIEW_COOKIE = 'gz_preview';

/** Typed outside a text field, within {@link SEQUENCE_WINDOW_MS}: opens the switcher. GoZig + release. */
export const OPEN_SEQUENCE = 'gzr';
export const SEQUENCE_WINDOW_MS = 1000;
/** Resting the pointer in the bottom-left corner this long opens it too, outside production. */
export const CORNER_HOLD_MS = 2000;

export function readPreview(cookie: string = document.cookie): ReleaseSide | null {
    const match = new RegExp(`(?:^|;\\s*)${PREVIEW_COOKIE}=(stable|canary)(?:;|$)`).exec(cookie);
    return match ? match[1] as ReleaseSide : null;
}

/** A session cookie, so a pin never outlives the browser session. Not HttpOnly: this switcher sets it. */
export function writePreview(side: ReleaseSide, secure: boolean = window.location.protocol === 'https:'): void {
    document.cookie = `${PREVIEW_COOKIE}=${side}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
}

/** Drop the pin: back to automatic placement, like any visitor. */
export function clearPreview(): void {
    document.cookie = `${PREVIEW_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export interface ReleaseBuilds {
    stable: string;
    canary: string;
}

/** Whether a release is in progress: the two probes reached different builds. */
export const releaseInProgress = (builds: ReleaseBuilds | null): boolean =>
    builds !== null && builds.stable !== builds.canary;

/** Both sides' builds, from the build file every pod serves; null when either probe fails. */
export async function fetchReleaseBuilds(fetchImpl: typeof fetch = (input, init) => fetch(input, init)): Promise<ReleaseBuilds | null> {
    const probe = async (value: '0' | '1') => {
        const response = await fetchImpl(`/kuder-build.txt?release=${value}`, {
            cache: 'no-store',
            // Omit cookies: a pinned visitor's own cookies would otherwise outrank the probe header at Envoy.
            credentials: 'omit',
            headers: { 'x-kuder-canary': value },
        });
        if (!response.ok) throw new Error(`build probe ${value}: ${response.status}`);
        return (await response.text()).trim();
    };
    try {
        const [stable, canary] = await Promise.all([probe('0'), probe('1')]);
        return stable && canary ? { stable, canary } : null;
    }
    catch {
        return null;
    }
}

/** A short build label: the first seven characters of a commit sha, other ids as they are. */
export const shortBuild = (build: string): string => (/^[0-9a-f]{40}$/.test(build) ? build.slice(0, 7) : build);

/** Whether a key event belongs to a text field, where typing the sequence must do nothing. */
export function isTyping(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** Tracks typed keys and reports when the last ones spell {@link OPEN_SEQUENCE} within the window. */
export function sequenceMatcher(sequence: string = OPEN_SEQUENCE, windowMs: number = SEQUENCE_WINDOW_MS) {
    let typed: { key: string; at: number }[] = [];
    return (key: string, at: number): boolean => {
        if (key.length !== 1) return false;
        typed = [...typed, { key: key.toLowerCase(), at }].filter(entry => at - entry.at <= windowMs).slice(-sequence.length);
        return typed.map(entry => entry.key).join('') === sequence;
    };
}
